/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import _ from 'lodash';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';

export const EntityType = {
  NATURAL_PERSON: 'NATURAL_PERSON',
  LEGAL_ENTITY: 'LEGAL_ENTITY',
  OTHER: 'OTHER',
};

export const SignerType = {
  NATURAL: 'natural',
  LEGAL: 'legal',
  OTHER: 'other',
};

/**
 * Parties configuration hook that exposes Party helper methods based on the Parties structure configuration.
 */
export function usePartiesService() {
  const { resource: partiesConfiguration } = useConfiguration(ConfigResourceProps.PARTIES_CONFIGURATION);

  /**
   * Flattens the structure objects of the Parties with 2 levels like Applicants and Representatives
   * to one level objects
   * {
   *   entityType:'NATURAL_PERSON'
   *   path:'applicants'
   *   role:'APPLICANT_NATURAL_PERSON'
   *   signatureCapacity:'APPLICANT'
   *   type:'APPLICANT'
   * }
   * @returns array with the flattened Party type objects
   */
  const getFlattenedPartyTypesFromConfig = () => {
    const partyTypes = _.values(partiesConfiguration?.partyTypes);
    return _.flatMap(partyTypes, (partyType) => {
      if (!partyType.subtypes) {
        return [partyType];
      }
      return _.keys(partyType.subtypes).map((key) => {
        return {
          path: partyType.path,
          type: partyType.type,
          canSign: partyType.canSign,
          ...partyType.subtypes[key],
        };
      });
    });
  };

  const getPartyTypeByRole = (role) => {
    const flattenPartyTypes = getFlattenedPartyTypesFromConfig();
    const partyType = flattenPartyTypes.find(partyType_ => partyType_.role === role);
    return partyType?.type;
  };

  const getEntityTypeByRole = (role) => {
    const flattenPartyTypes = getFlattenedPartyTypesFromConfig();
    const partyType = flattenPartyTypes.find(partyType_ => partyType_.role === role);
    return partyType?.entityType;
  };

  const getSignatureCapacityByRole = (role) => {
    const flattenPartyTypes = getFlattenedPartyTypesFromConfig();
    const partyType = flattenPartyTypes.find(partyType_ => partyType_.role === role);
    return partyType?.signatureCapacity;
  };

  const isPartyCompany = (role) => {
    return getEntityTypeByRole(role) === EntityType.LEGAL_ENTITY;
  };

  const getNameFragmentByRole = (role) => {
    return isPartyCompany(role) ? SignerType.LEGAL : SignerType.NATURAL;
  };

  const getPartyTypesWhichCanSign = () => {
    return _.keys(partiesConfiguration?.partyTypes).filter(
      partyType => partiesConfiguration?.partyTypes?.[partyType]?.canSign,
    );
  };

  const getPartyTypeByPath = (path) => {
    return Object.keys(partiesConfiguration?.partyTypes).find((key) => {
      return partiesConfiguration?.partyTypes?.[key]?.path === path;
    });
  };

  return {
    getPartyTypeByRole,
    getEntityTypeByRole,
    getSignatureCapacityByRole,
    isPartyCompany,
    getNameFragmentByRole,
    getPartyTypesWhichCanSign,
    getPartyTypeByPath,
  };
}
